import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { WaterfallChart } from '@/components/charts/WaterfallChart';
import { Lock, TrendingUp, TrendingDown, Info, PieChart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { AttributionBreakdown } from '@/hooks/useAttribution';

interface Props {
  attribution: AttributionBreakdown | null;
  isPro: boolean;
  isLoading?: boolean;
  className?: string;
}

export function PerformanceAttributionSection({ attribution, isPro, isLoading, className }: Props) {
  const navigate = useNavigate();

  // Build waterfall chart data
  const waterfallData = attribution ? [
    { name: "Market Beta", value: attribution.marketBeta },
    { name: "Leverage", value: attribution.leverageContribution },
    { name: "Timing Alpha", value: attribution.timingAlpha },
    { name: "Sizing Alpha", value: attribution.sizingAlpha },
    { name: "Fees", value: attribution.feesDrag },
    { name: "Funding", value: attribution.fundingDrag },
    { name: "Total PnL", value: attribution.totalPnl, isTotal: true },
  ] : [];

  // Generate insight from attribution data
  const generateInsight = () => {
    if (!attribution) return null;

    const { totalPnl, marketBeta, leverageContribution, timingAlpha, feesDrag } = attribution;
    
    // Identify the largest contributor
    const contributors = [
      { name: 'market direction', value: marketBeta },
      { name: 'leverage', value: leverageContribution },
      { name: 'timing skill', value: timingAlpha },
    ].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    const largestContributor = contributors[0];
    
    if (totalPnl > 0 && largestContributor.value > 0) {
      const percentage = Math.abs(largestContributor.value / totalPnl * 100);
      if (percentage > 40) {
        return {
          type: 'info' as const,
          message: `${percentage.toFixed(0)}% of your gains came from ${largestContributor.name}. ${
            largestContributor.name === 'timing skill' 
              ? 'Your entry/exit decisions are adding significant alpha.' 
              : largestContributor.name === 'leverage'
                ? 'Consider if this risk level is sustainable.'
                : 'You benefited from favorable market conditions.'
          }`,
        };
      }
    }

    if (Math.abs(feesDrag) > Math.abs(totalPnl) * 0.2 && totalPnl !== 0) {
      return {
        type: 'warning' as const,
        message: `Fees are consuming ${Math.abs(feesDrag / totalPnl * 100).toFixed(0)}% of your gross PnL. Consider reducing trade frequency or using limit orders.`,
      };
    }

    if (timingAlpha < 0 && Math.abs(timingAlpha) > Math.abs(totalPnl) * 0.3) {
      return {
        type: 'warning' as const,
        message: `Poor timing is costing you $${Math.abs(timingAlpha).toFixed(2)}. Focus on improving your entry and exit timing.`,
      };
    }

    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn("space-y-4", className)}>
        <div>
          <h2 className="text-xl font-bold mb-1">Performance Attribution</h2>
          <p className="text-sm text-muted-foreground">Loading attribution analysis...</p>
        </div>
        <div className="h-[400px] bg-muted/30 rounded-lg animate-pulse" />
      </div>
    );
  }

  // Pro-gated blur overlay for non-Pro users
  if (!isPro) {
    return (
      <div className={cn("relative", className)}>
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
          <Card className="max-w-md border-primary/20 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Pro Feature Locked
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Unlock performance attribution to see exactly where your PnL came from:
                market direction, leverage multiplier, timing skill, or fees. Essential
                for understanding what's actually driving your results.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-4 text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  Market Beta
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  Leverage Effect
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  Timing Alpha
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-rose-500" />
                  Fee Drag
                </div>
              </div>
              <Button onClick={() => navigate('/pricing')} className="w-full">
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
        <div className="blur-sm pointer-events-none opacity-50">
          <div className="h-[400px] bg-muted/20 rounded-lg flex items-center justify-center">
            <PieChart className="w-16 h-16 text-muted-foreground/30" />
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!attribution) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="h-5 w-5" />
            Performance Attribution
          </CardTitle>
          <CardDescription>No attribution data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const insight = generateInsight();

  const formatCurrency = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    if (Math.abs(value) >= 1000) {
      return `${sign}$${(value / 1000).toFixed(1)}k`;
    }
    return `${sign}$${value.toFixed(2)}`;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Waterfall Chart */}
      <WaterfallChart
        data={waterfallData}
        title="Performance Attribution"
        subtitle="Decomposition of your PnL by source"
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Market Beta</p>
            <p className={cn(
              "text-xl font-bold font-mono",
              attribution.marketBeta >= 0 ? "text-emerald-500" : "text-rose-500"
            )}>
              {formatCurrency(attribution.marketBeta)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">From market direction</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Timing Alpha</p>
            <p className={cn(
              "text-xl font-bold font-mono",
              attribution.timingAlpha >= 0 ? "text-emerald-500" : "text-rose-500"
            )}>
              {formatCurrency(attribution.timingAlpha)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">From entry/exit skill</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Leverage Effect</p>
            <p className={cn(
              "text-xl font-bold font-mono",
              attribution.leverageContribution >= 0 ? "text-emerald-500" : "text-rose-500"
            )}>
              {formatCurrency(attribution.leverageContribution)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">From leverage multiplier</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Fee & Funding Drag</p>
            <p className="text-xl font-bold font-mono text-rose-500">
              {formatCurrency(attribution.feesDrag + attribution.fundingDrag)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Cost of trading</p>
          </CardContent>
        </Card>
      </div>

      {/* Insight Alert */}
      {insight && (
        <Alert variant={insight.type === 'warning' ? 'destructive' : 'default'}>
          {insight.type === 'warning' ? (
            <TrendingDown className="h-4 w-4" />
          ) : (
            <Info className="h-4 w-4" />
          )}
          <AlertTitle>{insight.type === 'warning' ? 'Opportunity' : 'Insight'}</AlertTitle>
          <AlertDescription>{insight.message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
